import { FunctionComponent, MutableRefObject, useState } from "react";

import {
	faChevronDown,
	faChevronUp,
	faFloppyDisk,
	faPaperPlane,
	faPen,
	faPlus,
	faSparkles,
	faTriangleExclamation,
	faUserPlus,
	faX,
} from "@fortawesome/pro-regular-svg-icons";

import { useTranslation } from "react-i18next";

import { Icon } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { InputField } from "@/components/InputField/InputField";
import { ListPerson } from "@/pages/Private/pages/List/schema/list";
import { classNames } from "@/utils/classNames";

import { Draft, DraftTiming } from "../utils/inboxDraft.types";
import { PendingInvitationView } from "../utils/pendingInvitation.util";
import {
	draftStripPreview,
	hasDraftChannelMismatch,
	hasMissingSubject,
} from "../utils/draftValidation.util";
import { useEditorResize } from "../utils/useEditorResize";
import { isDraftTestable } from "../utils/draftToTestMessageRequest.util";
import { resolveSenderTier } from "../utils/resolveSenderTier.util";
import { resolveSenderContext } from "../utils/resolveSenderContext.util";
import { resolveDraftPreviewHtml } from "../utils/resolveDraftPreviewHtml";
import { SenderRecipientDropdown, SenderRecipientOption } from "./SenderRecipientDropdown";
import { DraftSenderWarning } from "./DraftSenderWarning";
import { DraftTimingRow } from "./DraftTimingRow";
import { InvitationPendingTag, PendingInvitationNotice } from "./PendingInvitationNotice";
import { EmailTagInput } from "./EmailTagInput";
import { IncludeSignatureToggle } from "./IncludeSignatureToggle";
import { InviteNoteCharCounter } from "./InviteNoteCharCounter";
import { VariablePicker } from "./VariablePicker";
import { ComposeMoreMenu } from "./ComposeMoreMenu";

import type { User } from "@/pages/Public/pages/Login/schema/login";

/** The active draft's sender as the picker names it ("Me", or a colleague's name). */
const senderOptionName = (draft: Draft | undefined, options: SenderRecipientOption[]) =>
	options.find((o) => o.type === "linkedin" && o.userId === draft?.from?.userId)?.displayName;

interface DraftEditorPanelProps {
	drafts: Draft[];
	activeDraftIndex: number;
	lead?: ListPerson;
	senderOptions: SenderRecipientOption[];
	recipientOptions: SenderRecipientOption[];
	sendingUsers: User[];
	suggestions: string[];
	variables: { key: string; label: string }[];
	portalContainer?: HTMLElement | null;
	/** Collapsed = a single strip with the live first line of the active draft. */
	isCollapsed: boolean;
	onExpand: () => void;
	onToggleCollapsed: () => void;
	inviteBaselineDays: number | null;
	activeIsInviteFollowUp: boolean;
	composerRef: MutableRefObject<HTMLTextAreaElement | null>;
	message: string;
	onMessageChange: (value: string) => void;
	onSend: () => void;
	onFromChange: (option: SenderRecipientOption) => void;
	onToChange: (option: SenderRecipientOption) => void;
	onTimingChange: (timing: DraftTiming) => void;
	onCcChange: (emails: string[]) => void;
	onBccChange: (emails: string[]) => void;
	onToggleCcBcc: () => void;
	onSubjectChange: (value: string) => void;
	/** The company's Include signature default, and the active draft's own choice (PREC-4401). */
	signatureDefault: boolean;
	onIncludeSignatureChange: (value: boolean) => void;
	onInsertVariable: (key: string) => void;
	onAddFollowUp: () => void;
	canSaveCopywritingDraft: boolean;
	isSavingCopywritingDraft: boolean;
	onSaveCopywritingDraft: () => void;
	renderTemplateButton: () => JSX.Element | null;
	isCopywritingActive: boolean;
	onOpenGeneratePanel: () => void;
	onOpenSendTest: () => void;
	/** The active draft waits for the sender's pending invitation (PREC-4216); null otherwise. */
	pendingInvitation?: PendingInvitationView | null;
	/** "Send from <colleague>" in the pending-invitation notice. */
	onSendFromColleague?: (option: SenderRecipientOption) => void;
}

/**
 * The sticky reply editor under the chat log (extracted from
 * `useAddInboxChat`): From/To/Send header, CC/BCC + subject rows, validation
 * warnings, the composer textarea with variable preview, and the draft
 * toolbar. When collapsed it renders as a single strip showing the live
 * first line of the active draft — click to expand (mockup `.se-strip`).
 */
export const DraftEditorPanel: FunctionComponent<DraftEditorPanelProps> = ({
	drafts,
	activeDraftIndex,
	lead,
	senderOptions,
	recipientOptions,
	sendingUsers,
	suggestions,
	variables,
	portalContainer,
	isCollapsed,
	onExpand,
	onToggleCollapsed,
	inviteBaselineDays,
	activeIsInviteFollowUp,
	composerRef,
	message,
	onMessageChange,
	onSend,
	onFromChange,
	onToChange,
	onTimingChange,
	onCcChange,
	onBccChange,
	onToggleCcBcc,
	onSubjectChange,
	signatureDefault,
	onIncludeSignatureChange,
	onInsertVariable,
	onAddFollowUp,
	canSaveCopywritingDraft,
	isSavingCopywritingDraft,
	onSaveCopywritingDraft,
	renderTemplateButton,
	isCopywritingActive,
	onOpenGeneratePanel,
	onOpenSendTest,
	pendingInvitation,
	onSendFromColleague,
}) => {
	const { t } = useTranslation();

	// Active-draft preview is collapsed by default; the user can expand it to see
	// the variables resolved to the actual data that will be sent.
	const [isPreviewExpanded, setIsPreviewExpanded] = useState<boolean>(false);

	// Drag the grip to resize the composer; a plain click on it collapses.
	const { heightPx, handleGripPointerDown } = useEditorResize({ onGripClick: onToggleCollapsed });

	const activeDraft = drafts[activeDraftIndex];
	const senderContext = resolveSenderContext(activeDraft?.from, sendingUsers);
	const isEmailDraft = activeDraft?.from?.type === "email";
	const includeSignature = isEmailDraft && (activeDraft?.includeSignature ?? signatureDefault);

	if (isCollapsed) {
		const preview = draftStripPreview(message);

		return (
			<div className="flex flex-col w-full mb-2 px-4 shrink-0" data-tour="draft-reply-editor">
				<div
					className="flex items-center gap-2 rounded-lg border border-border-light bg-brand-white px-3 py-2.5 cursor-pointer hover:bg-gray-50 shadow-[0_-4px_14px_rgba(16,24,40,0.06)]"
					onClick={onExpand}
				>
					<Icon className="w-3 h-3 shrink-0 text-blue-action" icon={faPen} />
					{preview ? (
						<span className="min-w-0 truncate text-xs text-text">{preview}</span>
					) : (
						<span className="text-xs italic text-text-meta">
							{t("inbox.editor.stripPlaceholder")}
						</span>
					)}
					<Icon className="ml-auto w-3 h-3 shrink-0 text-text-meta" icon={faChevronUp} />
				</div>
			</div>
		);
	}

	return (
		<div
			className="flex flex-col w-full mb-2 px-4 shrink-0"
			data-editor-panel
			data-tour="draft-reply-editor"
		>
			{/* Editor header: From / To */}
			<div className="flex flex-col bg-brand-white border border-border-light rounded-t-lg">
				{/* Resize grip: drag to resize the composer, click to collapse (mockup .se-grip) */}
				<div
					className="flex cursor-ns-resize touch-none justify-center pt-1.5 pb-0.5"
					onPointerDown={handleGripPointerDown}
				>
					<span className="h-1 w-9 rounded-full bg-border-light" />
				</div>
				<div className="flex flex-wrap items-center gap-2 px-3 py-2">
					<div className="flex items-center gap-1">
						<span className="text-xs text-text-meta">From:</span>
						<SenderRecipientDropdown
							options={senderOptions}
							portalContainer={portalContainer}
							showLinkedinTier={!!activeDraft?.isInviteNote}
							value={activeDraft?.from?.label ?? "Select sender"}
							onSelect={onFromChange}
						/>
					</div>
					<div className="flex items-center gap-1">
						<span className="text-xs text-text-meta">To:</span>
						<SenderRecipientDropdown
							options={recipientOptions}
							portalContainer={portalContainer}
							value={activeDraft?.to?.label ?? "Select recipient"}
							onSelect={onToChange}
						/>
					</div>
					{pendingInvitation && <InvitationPendingTag />}

					<button
						className="ml-auto p-1 hover:bg-blue-action/10 rounded text-blue-action cursor-pointer"
						title={t("inbox.editor.collapse")}
						type="button"
						onClick={onToggleCollapsed}
					>
						<Icon className="w-3 h-3" icon={faChevronDown} />
					</button>
				</div>
				{/* Send timing / CC|BCC — always on its own line */}
				<DraftTimingRow
					activeDraft={activeDraft}
					activeIsInviteFollowUp={activeIsInviteFollowUp}
					inviteBaselineDays={inviteBaselineDays}
					portalContainer={portalContainer}
					waitsForAcceptance={!!pendingInvitation}
					onTimingChange={onTimingChange}
					onToggleCcBcc={onToggleCcBcc}
				/>
			</div>

			{/* CC / BCC fields */}

			<>
				{activeDraft?.from?.type === "email" && activeDraft?.showCcBcc && (
					<div className="flex flex-col gap-1 px-3 py-2 bg-brand-white border-x border-border-light">
						<div className="flex items-center gap-2">
							<span className="text-xs text-text-meta w-8 shrink-0">CC:</span>
							<EmailTagInput
								emails={activeDraft?.cc ?? []}
								name="cc"
								placeholder="e.g. colleague@company.com"
								onChange={onCcChange}
							/>
						</div>
						<div className="flex items-center gap-2">
							<span className="text-xs text-text-meta w-8 shrink-0">BCC:</span>
							<EmailTagInput
								emails={activeDraft?.bcc ?? []}
								name="bcc"
								placeholder="e.g. tracker@company.com"
								onChange={onBccChange}
							/>
						</div>
					</div>
				)}

				{/* Subject line — shown for email channel */}
				{activeDraft?.from?.type === "email" && (
					<div
						className={classNames(
							"flex items-center gap-2 px-3 py-2 bg-brand-white border-x",
							hasMissingSubject(activeDraft) ? "border-red-500" : "border-border-light"
						)}
					>
						<span
							className={classNames(
								"text-xs shrink-0",
								hasMissingSubject(activeDraft) ? "text-red-500" : "text-text-meta"
							)}
						>
							Subject:
						</span>
						<input
							className={classNames(
								"flex-1 text-xs bg-transparent border-none outline-none text-text placeholder:text-text-meta",
								hasMissingSubject(activeDraft) ? "placeholder:text-red-400" : ""
							)}
							placeholder="Enter email subject..."
							type="text"
							value={activeDraft?.subject ?? ""}
							onChange={(e) => onSubjectChange(e.target.value)}
						/>
					</div>
				)}

				{isEmailDraft && (
					<IncludeSignatureToggle
						checked={includeSignature}
						hasSignature={!!senderContext.signature?.trim()}
						onChange={onIncludeSignatureChange}
					/>
				)}

				<DraftSenderWarning drafts={drafts} senderOptions={senderOptions} />
				{/* Missing subject warning */}
				{activeDraft && hasMissingSubject(activeDraft) && (
					<div className="flex items-center gap-2 px-3 py-2 bg-red-50 border-x border-red-500 text-red-600">
						<Icon className="w-3.5 h-3.5 shrink-0" icon={faTriangleExclamation} />
						<span className="text-xs">Subject line is required for email messages.</span>
					</div>
				)}

				{/* Channel mismatch warning */}
				{activeDraft && hasDraftChannelMismatch(activeDraft) && (
					<div className="flex items-center gap-2 px-3 py-2 bg-red-50 border-x border-border-light text-red-600">
						<Icon className="w-3.5 h-3.5 shrink-0" icon={faTriangleExclamation} />
						<span className="text-xs">
							Sender and recipient must use the same channel. You cannot send from{" "}
							{activeDraft?.from?.type} to {activeDraft?.to?.type}.
						</span>
					</div>
				)}

				{pendingInvitation && onSendFromColleague && (
					<PendingInvitationNotice
						leadFirstName={lead?.prospect?.firstName}
						senderDisplayName={senderOptionName(activeDraft, senderOptions)}
						view={pendingInvitation}
						onSendFromColleague={onSendFromColleague}
					/>
				)}

				{/* Invite note indicator */}
				{activeDraft?.isInviteNote && (
					<div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border-x border-border-light">
						<span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-semibold uppercase tracking-wide">
							<Icon className="w-3 h-3" icon={faUserPlus} />
							LinkedIn Invite
						</span>
						<span className="text-xs text-amber-700">
							This message will be sent as a LinkedIn connection request.
						</span>
					</div>
				)}

				<div className="w-full flex flex-row gap-2">
					<InputField
						className={classNames(
							// !text-sm: match the page's chat-bubble size instead of InputField's
							// default text-ssm (16px) — PREC-3850 compact reply editor.
							"w-full bg-blue-action-200 !min-h-[30px] !text-sm",
							activeDraft?.showCcBcc ? "!rounded-t-none border-t-0" : "!rounded-t-none border-t-0"
						)}
						handleChange={(e) => {
							onMessageChange(e.target.value);
						}}
						iconInside={
							message.length > 0 ? (
								<Icon
									className="w-4 h-4 text-text-meta cursor-pointer hover:text-brand-light"
									icon={faX}
									onClick={() => {
										onMessageChange("");
									}}
								/>
							) : undefined
						}
						heightPx={heightPx}
						iconInsidePosition="right"
						inputRef={composerRef}
						// ~5 lines at text-sm; longer drafts scroll internally. The drag grip
						// (heightPx) still lets the user enlarge past this.
						maxAutoGrowHeightPx={120}
						multiline={true}
						name="message"
						placeholder={
							activeDraft?.isInviteNote
								? "Add an optional note to your invite..."
								: "Reply to the message"
						}
						value={message}
						onKeyDown={(e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
							if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
								onSend();
							}
						}}
					/>
				</div>

				{/* Live preview: variables resolved to the actual data that will be sent.
				    Collapsed by default; toggled via the header. */}
				{(/\{[^{}]+\}/.test(message) || (includeSignature && !!senderContext.signature)) && (
					<div className="px-3 pt-2">
						<button
							className="flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide text-text-meta hover:text-blue-action cursor-pointer"
							type="button"
							onClick={() => setIsPreviewExpanded((prev) => !prev)}
						>
							<Icon
								className="w-2.5 h-2.5"
								icon={isPreviewExpanded ? faChevronUp : faChevronDown}
							/>
							{t("inbox.variable.previewLabel")}
						</button>
						{isPreviewExpanded && (
							<div
								className="mt-1 text-sm text-text break-words max-h-[200px] overflow-y-auto rounded-md border border-border-light bg-brand-white px-3 py-2"
								dangerouslySetInnerHTML={{
									__html: resolveDraftPreviewHtml(
										message,
										lead,
										suggestions,
										senderContext,
										t("inbox.variable.unsubscribeChip"),
										{ isLinkedin: activeDraft?.from?.type === "linkedin", includeSignature }
									),
								}}
							/>
						)}
					</div>
				)}

				{/* Invite note character counter */}
				{activeDraft?.isInviteNote && (
					<div className="flex justify-end px-3 pt-1">
						<InviteNoteCharCounter
							charCount={message.length}
							tier={resolveSenderTier(activeDraft?.from?.userId, senderOptions)}
						/>
					</div>
				)}

				<div className="flex flex-row flex-wrap gap-2 w-full justify-end">
					<div className="w-fit">
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							image={<Icon className="mr-2" icon={faPlus} />}
							size={ButtonSize.XS}
							title="Add Follow-up"
							onClick={onAddFollowUp}
						/>
					</div>
					{canSaveCopywritingDraft && (
						<div className="w-fit">
							<Button
								color={ButtonColor.ACTION_SECONDARY}
								image={<Icon className="mr-2" icon={faFloppyDisk} />}
								isLoading={isSavingCopywritingDraft}
								size={ButtonSize.XS}
								title={t("inbox.copywritingDraft.save")}
								onClick={onSaveCopywritingDraft}
							/>
						</div>
					)}
					<VariablePicker
						portalContainer={portalContainer}
						variables={variables}
						onInsert={onInsertVariable}
					/>
					{renderTemplateButton()}
					<ComposeMoreMenu
						items={[
							...(isCopywritingActive
								? [
										{
											id: "tell-ai",
											label: t("inbox.composeMenu.tellAiWhatToWrite"),
											icon: faSparkles,
											onSelect: onOpenGeneratePanel,
										},
									]
								: []),
							{
								id: "send-test",
								label: t("inbox.sendTest.triggerButton"),
								icon: faPaperPlane,
								disabled: !isDraftTestable(activeDraft),
								onSelect: onOpenSendTest,
							},
						]}
						portalContainer={portalContainer}
					/>
				</div>
			</>
		</div>
	);
};
